import type { ComputedSlot, TimeWindow } from '@vagusx/clients';
import {
  type PreferredDaysValue,
  mergeWindows,
  minToTime,
  timeToMin,
} from '@/components/custom/preferred-days-grid';
import { extractDate } from './slot-grouping';

/** Canonical Mon→Sun day order, matching the grid widget's `days`/`dayTimes` keys. */
const DAY_ABBR = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/**
 * Last-resort grid step, used only when neither an explicit appointment-type
 * duration nor any slot is available to size the cells. The range it applies to
 * is cosmetic, so any sensible cell size works.
 */
const DEFAULT_STEP_MINUTES = 15;

/**
 * Default visible range, hour-aligned (07:00–19:00). The grid is anchored to this
 * fixed window so the popup keeps a constant height as you page between weeks, and
 * so each row starts on a clean hour (the grid widget only prints a time label on
 * rows whose start lands on the hour). A week with slots outside the window
 * expands it — floored/ceil'd to whole hours so labels keep landing — rather than
 * clipping; the common mid-day week stays at the default size.
 */
const DEFAULT_START_MIN = 7 * 60; // 07:00
const DEFAULT_END_MIN = 19 * 60; // 19:00

const floorToHour = (min: number): number => Math.floor(min / 60) * 60;
const ceilToHour = (min: number): number => Math.ceil(min / 60) * 60;

/** Extract "HH:MM" (24h) of an ISO instant in a given IANA timezone. */
function extractTimeHHMM(isoString: string, timezone: string): string {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  return formatter.format(new Date(isoString)); // en-GB + h23 gives "HH:MM"
}

/** "YYYY-MM-DD" → Mon-first weekday abbreviation. */
function dateToAbbr(date: string): (typeof DAY_ABBR)[number] {
  const [year, month, day] = date.split('-').map(Number);
  // Build the date at UTC noon so getUTCDay reflects the calendar date itself,
  // independent of the runtime's local timezone.
  const dow = new Date(Date.UTC(year!, month! - 1, day!, 12)).getUTCDay(); // 0=Sun..6=Sat
  return DAY_ABBR[(dow + 6) % 7]!;
}

/** Where one slot sits on the week grid: its clinic-local day and window. */
export interface SlotGridCell {
  /** The clinic-local calendar date, "YYYY-MM-DD". */
  date: string;
  /** Mon-first weekday abbreviation, the grid's day key. */
  day: (typeof DAY_ABBR)[number];
  /** Clinic-local "HH:MM" start and end. */
  startTime: string;
  endTime: string;
}

/**
 * One slot's day and window, read in its clinic's timezone: the cells
 * {@link slotsToWeekValue} paints for it, and the ones a highlight lights up.
 */
export function slotGridCell(
  slot: Pick<ComputedSlot, 'startTime' | 'endTime' | 'locationTimezone'>
): SlotGridCell {
  const date = extractDate(slot.startTime, slot.locationTimezone);
  const startTime = extractTimeHHMM(slot.startTime, slot.locationTimezone);
  // A slot ending at/after clinic-local midnight reads as "00:00"/"00:15",
  // which is numerically before its start — an inverted window the grid would
  // silently drop. Clamp the end to end-of-day ("24:00") so the block stays
  // on its own day and renders.
  const rawEnd = extractTimeHHMM(slot.endTime, slot.locationTimezone);
  const endTime = timeToMin(rawEnd) <= timeToMin(startTime) ? '24:00' : rawEnd;
  return { date, day: dateToAbbr(date), startTime, endTime };
}

/**
 * Project one calendar week of computed slots onto the grid widget's
 * `{ days, dayTimes, grid }` value. Each slot's start/end are read in its
 * clinic-local timezone, grouped by weekday, and merged into contiguous
 * windows. The grid view is anchored to a fixed 07:00–19:00 window (so the popup
 * height stays constant across weeks), expanding only to take in any slot that
 * falls outside it.
 *
 * Grid cells are sized to the appointment type's slot duration so a 30-min type
 * renders 30-min rows and a 15-min type renders 15-min rows. Prefer the explicit
 * `durationMinutes` (known even on empty weeks); otherwise read it off the first
 * slot — every slot in one preview shares a provider + appointment type, so they
 * carry the same duration — and only then fall back to a default cell size.
 *
 * Within a single week each weekday is unique, so the weekday projection is
 * unambiguous — callers pair this with `columnDateLabels` to show real dates.
 */
export function slotsToWeekValue(
  slots: ComputedSlot[],
  durationMinutes?: number
): PreferredDaysValue {
  const stepMinutes = durationMinutes ?? slots[0]?.durationMinutes ?? DEFAULT_STEP_MINUTES;
  const windowsByDay = new Map<string, TimeWindow[]>();

  for (const slot of slots) {
    const { day: abbr, startTime, endTime } = slotGridCell(slot);
    const existing = windowsByDay.get(abbr);
    if (existing) existing.push({ startTime, endTime });
    else windowsByDay.set(abbr, [{ startTime, endTime }]);
  }

  const days: string[] = [];
  const dayTimes: Record<string, TimeWindow[]> = {};
  let minStart = Infinity;
  let maxEnd = -Infinity;

  for (const abbr of DAY_ABBR) {
    const windows = windowsByDay.get(abbr);
    if (!windows || windows.length === 0) continue;
    const merged = mergeWindows(windows);
    days.push(abbr);
    dayTimes[abbr] = merged;
    for (const w of merged) {
      minStart = Math.min(minStart, timeToMin(w.startTime));
      maxEnd = Math.max(maxEnd, timeToMin(w.endTime));
    }
  }

  // Anchor to the fixed default window, widening (snapped to whole hours, so row
  // labels keep landing on the hour) only when a slot sits outside it. With no
  // slots the window is exactly the default. minToTime clamps to 00:00–24:00.
  const startMin =
    days.length === 0 ? DEFAULT_START_MIN : Math.min(DEFAULT_START_MIN, floorToHour(minStart));
  const endMin =
    days.length === 0 ? DEFAULT_END_MIN : Math.max(DEFAULT_END_MIN, ceilToHour(maxEnd));
  return {
    days,
    dayTimes,
    grid: { startTime: minToTime(startMin), endTime: minToTime(endMin), stepMinutes },
  };
}
